// Point-A-to-point-B road routing (client-requested: "Google Maps jaisa
// route selection" in Dispatch/Driver navigation, Visit Planner's location
// picker, and Live GPS Radar — all three previously either had no route
// preview at all, or drew a straight "as the crow flies" line between two
// points instead of a real road route).
//
// Uses OSRM's free public demo routing server rather than a paid API — this
// codebase has no Google Maps API key configured anywhere (it's built on
// OpenStreetMap/Leaflet throughout, see GpsLocationPickerMap.tsx), and adding
// a billed key is a decision for whoever owns that account, not something to
// wire in unasked. OSRM's demo server has no key requirement and is
// widely used for exactly this kind of light, non-commercial-volume routing.
// If it's ever unavailable, every caller here falls back to a straight line
// rather than failing — a less precise preview beats no preview.

export interface LatLng {
  lat: number;
  lng: number;
}

export interface RouteResult {
  /** [lat, lng] pairs in path order, ready for Leaflet's L.polyline(). */
  path: [number, number][];
  distanceKm: number;
  durationMin: number;
  /** false when this is the straight-line fallback, not a real road route. */
  isRoad: boolean;
}

const OSRM_BASE = 'https://router.project-osrm.org/route/v1/driving';

function straightLineFallback(origin: LatLng, destination: LatLng): RouteResult {
  // Haversine — good enough for a fallback distance estimate, not meant to
  // match road distance.
  const R = 6371;
  const dLat = ((destination.lat - origin.lat) * Math.PI) / 180;
  const dLng = ((destination.lng - origin.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((origin.lat * Math.PI) / 180) * Math.cos((destination.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  const distanceKm = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return {
    path: [
      [origin.lat, origin.lng],
      [destination.lat, destination.lng],
    ],
    distanceKm: Math.round(distanceKm * 10) / 10,
    // Rough placeholder assuming ~30 km/h average city/industrial-road speed.
    durationMin: Math.round((distanceKm / 30) * 60),
    isRoad: false,
  };
}

/**
 * Fetches a real road route between two points. Never throws — returns the
 * straight-line fallback on any network error, timeout, or empty result, so
 * a caller can always draw SOMETHING without its own try/catch.
 */
export async function fetchRoute(origin: LatLng, destination: LatLng, signal?: AbortSignal): Promise<RouteResult> {
  try {
    const url = `${OSRM_BASE}/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=full&geometries=geojson`;
    const res = await fetch(url, { signal });
    if (!res.ok) return straightLineFallback(origin, destination);
    const data = await res.json();
    const route = data?.routes?.[0];
    const coords = route?.geometry?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return straightLineFallback(origin, destination);
    return {
      // GeoJSON is [lng, lat]; Leaflet wants [lat, lng].
      path: coords.map(([lng, lat]: [number, number]) => [lat, lng]),
      distanceKm: Math.round((route.distance / 1000) * 10) / 10,
      durationMin: Math.round(route.duration / 60),
      isRoad: true,
    };
  } catch {
    return straightLineFallback(origin, destination);
  }
}
